import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { ContentSection } from 'site-modules/shared/components/content-section/content-section';
import { InventoryEntities } from 'client/data/models/inventory';
import { getDisplayInventoryTypeName } from 'site-modules/shared/utils/publication-states';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { CPO_JUMP_LINK_LOCATION } from 'site-modules/shared/constants/inventory/vdp-navigation';
import { getVinOverviewId } from 'site-modules/shared/utils/inventory/vdp-utils';
import { adjustTimezone, createDate } from 'site-modules/shared/utils/date-utils';
import { appendTrademarkCharacter } from 'site-modules/shared/utils/inventory-utils/append-trademark-character';
import { QuickRCode } from 'client/site-modules/shared/components/qr-code/qr-code';
import { VdpActions } from 'site-modules/inventory/components/vdp-actions/vdp-actions';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { isPartnerDealer } from 'site-modules/shared/utils/dealer-details-utils';
import dateFormat from 'dateformat';
import { getQuery } from 'client/utils/location';
import { showTrimName } from 'site-modules/shared/utils/show-trim-name';

import './vin-overview.scss';

const SITE_URL = 'https://www.edmunds.com';

function VinOverviewUI({
  vinData = {},
  isCpo = false,
  isMobile = false,
  isVlaPage = false,
  showQRCode = false,
  location = {},
  withSaveBtn = false,
  isVdp = false,
}) {
  const { type } = vinData;
  const { make, model, year, trim } = get(vinData, 'vehicleInfo.styleInfo', {});
  const vinOverviewTrackingParent = getVinOverviewId(isCpo);
  const { pathname } = location;
  const radius = getQuery(location).radius;
  const utmQuery = 'utm_source=edmunds&utm_medium=qr_code';
  const inTransit = get(vinData, 'inTransit', false);
  const isPartner = isPartnerDealer(vinData);
  const publishDate = get(vinData, 'publishDate');
  const qrCodeValue = `${SITE_URL}${pathname}${radius ? `?radius=${radius}&${utmQuery}` : `?${utmQuery}`}`;
  const displayTrim = showTrimName(make, trim) ? trim : '';
  const title = appendTrademarkCharacter({
    make,
    str: `${year} ${make} ${model} ${displayTrim}`,
  });

  return (
    <ContentSection
      className="vin-overview my-1_5 my-md-0 py-0 py-md-1 px-0_25 px-md-0"
      dataTrackingParent={vinOverviewTrackingParent}
      lineClassName="hidden-md-down mt-1"
      line={false}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <div
            className={classNames('text-gray-darker', {
              small: isMobile,
              medium: !isMobile,
            })}
          >
            {isCpo ? (
              <span>
                Certified Pre-Owned
                {!isVlaPage && (
                  <ScrollLink
                    scrollConfig={{ offset: SCROLL_OFFSET }}
                    className="text-primary-darker ms-0_5"
                    to={CPO_JUMP_LINK_LOCATION}
                    data-tracking-id="view_cpo_section"
                  >
                    See program details
                  </ScrollLink>
                )}
              </span>
            ) : (
              getDisplayInventoryTypeName(type)
            )}
          </div>

          <h1 className="d-inline-block mb-0 heading-2 mt-0_25">{title}</h1>
        </div>
        {withSaveBtn && (
          <div className="align-self-baseline">
            <VdpActions vinData={vinData} spinnerSize={16} trackingParent="vdp-navigation" />
          </div>
        )}
        {showQRCode && <QuickRCode value={qrCodeValue} />}
      </div>
      {isVdp && isPartner && inTransit && (
        <div className="row mx-0 in-transit-message px-1 py-1 mt-1 mb-0_5 rounded-12 bg-blue-100 text-cool-gray-40 align-items-center">
          <div className="col-auto d-inline-flex circle-icon-wrapper bg-blue-50 rounded-circle align-items-center justify-content-center">
            <i className="icon-right25 text-white" aria-hidden="true" />
          </div>
          <span className="col ps-0_75 pe-0">
            <b>This vehicle may be in transit</b>
            {isNew(type) ? ' from the factory to the dealer.' : ' to the dealer.'}
            {publishDate &&
              ` Listing details last updated ${dateFormat(
                adjustTimezone(createDate(get(vinData, 'publishDate'))),
                'mm/dd/yyyy'
              )}.`}
          </span>
        </div>
      )}
    </ContentSection>
  );
}

VinOverviewUI.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  isCpo: PropTypes.bool,
  isMobile: PropTypes.bool,
  isVlaPage: PropTypes.bool,
  showQRCode: PropTypes.bool,
  location: PropTypes.shape({}),
  withSaveBtn: PropTypes.bool,
  isVdp: PropTypes.bool,
};

const mapStateToProps = state => ({
  location: get(state, 'pageContext.location'),
});

export const VinOverview = connect(mapStateToProps)(VinOverviewUI);
